import { Fragment, useEffect, useMemo, useState } from "react";
import { metricStatus, statusOf } from "../lib/status";
import { I, MetricCell, Sparkline, StatusBadge } from "./primitives";

export function Dashboard({
  servers,
  thresholds,
  pulses,
  search,
  setSearch,
  statusFilter,
  roleFilter,
  setRoleFilter,
  view,
  density,
  onOpenServer,
  onDeleteServer,
  onEditServer,
  onAddServer,
}) {
  const [sortKey, setSortKey] = useState("cpu");
  const [sortDir, setSortDir] = useState("desc");
  const [menuFor, setMenuFor] = useState(null);

  useEffect(() => {
    const onDoc = () => setMenuFor(null);
    if (menuFor) {
      document.addEventListener("click", onDoc);
      return () => document.removeEventListener("click", onDoc);
    }
  }, [menuFor]);

  const roleOptions = useMemo(() => {
    const set = new Set(servers.map((s) => s.role).filter(Boolean));
    return Array.from(set);
  }, [servers]);

  const filtered = useMemo(() => {
    let list = servers.map((s) => ({ ...s, status: statusOf(s, thresholds) }));
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          (s.ip || "").toLowerCase().includes(q) ||
          (s.host || "").toLowerCase().includes(q) ||
          (s.tag || "").toLowerCase().includes(q),
      );
    }
    if (statusFilter !== "all") {
      list = list.filter((s) => s.status === statusFilter);
    }
    if (roleFilter) {
      list = list.filter((s) => s.role === roleFilter);
    }
    const order = { crit: 3, warn: 2, ok: 1, offline: 0 };
    list.sort((a, b) => {
      let cmp = 0;
      if (sortKey === "status") cmp = order[a.status] - order[b.status];
      else if (sortKey === "name") cmp = a.name.localeCompare(b.name);
      else if (sortKey === "cpu") cmp = (a.cpu ?? -1) - (b.cpu ?? -1);
      else if (sortKey === "ram") cmp = (a.ram ?? -1) - (b.ram ?? -1);
      else if (sortKey === "disk") cmp = (a.disk ?? -1) - (b.disk ?? -1);
      return sortDir === "desc" ? -cmp : cmp;
    });
    return list;
  }, [servers, thresholds, search, statusFilter, roleFilter, sortKey, sortDir]);

  const setSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === "desc" ? "asc" : "desc");
    else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const sortIndicator = (key) =>
    sortKey === key ? (
      <span style={{ marginLeft: 4, color: "var(--accent)" }}>{sortDir === "desc" ? "▾" : "▴"}</span>
    ) : null;

  return (
    <Fragment>
      <div className="toolbar">
        <div className="input" style={{ width: 280 }}>
          <I.search />
          <input
            placeholder="Cari server, hostname, IP, tag…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button className="chip-x" onClick={() => setSearch("")} title="Clear">
              <I.xSmall />
            </button>
          )}
        </div>

        <div style={{ position: "relative" }}>
          <button
            className="btn btn-ghost"
            style={{ borderColor: "var(--border)" }}
            onClick={(e) => {
              e.stopPropagation();
              setMenuFor(menuFor === "role" ? null : "role");
            }}
          >
            <I.filter />
            <span>{roleFilter ? `Role: ${roleFilter}` : "Filter Role"}</span>
            <I.chevronDown />
          </button>
          {menuFor === "role" && (
            <div className="menu" style={{ top: 38, left: 0 }}>
              <button
                className="menu-item"
                onClick={() => {
                  setRoleFilter(null);
                  setMenuFor(null);
                }}
              >
                <span style={{ width: 13, display: "inline-block" }}>{!roleFilter && <I.check />}</span>
                <span>Semua role</span>
              </button>
              <div className="menu-divider" />
              {roleOptions.map((r) => (
                <button
                  key={r}
                  className="menu-item"
                  onClick={() => {
                    setRoleFilter(r);
                    setMenuFor(null);
                  }}
                >
                  <span style={{ width: 13, display: "inline-block" }}>{roleFilter === r && <I.check />}</span>
                  <span>{r}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {(roleFilter || statusFilter !== "all" || search) && (
          <div className="chip">
            <span>
              {filtered.length} dari {servers.length} server
            </span>
            <button
              className="chip-x"
              onClick={() => {
                setSearch("");
                setRoleFilter(null);
              }}
            >
              <I.xSmall />
            </button>
          </div>
        )}

        <div style={{ flex: 1 }} />

        <button className="btn btn-primary" onClick={onAddServer}>
          <I.plus />
          <span>Tambah Server</span>
        </button>
      </div>

      {view === "table" ? (
        <div className="table-wrap">
          <table className={`servers density-${density}`}>
            <thead>
              <tr>
                <th className="sortable" onClick={() => setSort("name")} style={{ minWidth: 240 }}>
                  Server{sortIndicator("name")}
                </th>
                <th className="sortable" onClick={() => setSort("cpu")} style={{ width: 200 }}>
                  CPU{sortIndicator("cpu")}
                </th>
                <th className="sortable" onClick={() => setSort("ram")} style={{ width: 200 }}>
                  RAM{sortIndicator("ram")}
                </th>
                <th className="sortable" onClick={() => setSort("disk")} style={{ width: 200 }}>
                  Disk{sortIndicator("disk")}
                </th>
                <th style={{ width: 90 }}>Trend (CPU)</th>
                <th className="sortable" onClick={() => setSort("status")} style={{ width: 130 }}>
                  Status{sortIndicator("status")}
                </th>
                <th style={{ width: 80 }} />
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <ServerRow
                  key={s.id}
                  server={s}
                  thresholds={thresholds}
                  pulse={pulses[s.id]}
                  onOpen={() => onOpenServer(s)}
                  onDelete={() => onDeleteServer(s)}
                  onEdit={() => onEditServer(s)}
                />
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="empty-state">
              <div style={{ fontSize: 28, marginBottom: 8 }}>∅</div>
              <div style={{ color: "var(--text-2)", marginBottom: 2 }}>
                {servers.length === 0 ? "Belum ada server" : "Tidak ada server cocok"}
              </div>
              <div className="text-sm">
                {servers.length === 0
                  ? "Klik “Tambah Server” untuk mendaftarkan server pertama."
                  : "Coba ubah filter atau kata kunci."}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="cards">
          {filtered.map((s) => (
            <ServerCard
              key={s.id}
              server={s}
              thresholds={thresholds}
              pulse={pulses[s.id]}
              onOpen={() => onOpenServer(s)}
              onDelete={() => onDeleteServer(s)}
              onEdit={() => onEditServer(s)}
            />
          ))}
          {filtered.length === 0 && (
            <div className="empty-state" style={{ gridColumn: "1 / -1" }}>
              {servers.length === 0 ? "Belum ada server" : "Tidak ada server cocok"}
            </div>
          )}
        </div>
      )}
    </Fragment>
  );
}

function ServerRow({ server: s, thresholds, pulse, onOpen, onDelete, onEdit }) {
  return (
    <tr data-pulse={pulse || undefined} onClick={onOpen}>
      <td>
        <div className="cell-server">
          <span className="server-status-pip" data-status={s.status} />
          <div>
            <div>
              <span className="server-name">{s.name}</span>
              <span className="server-tag" data-role={s.role}>{s.tag}</span>
            </div>
            <div className="server-addr">{s.ip} · {s.region}</div>
          </div>
        </div>
      </td>
      <td><MetricCell value={s.cpu} threshold={thresholds.cpu} /></td>
      <td><MetricCell value={s.ram} threshold={thresholds.ram} /></td>
      <td><MetricCell value={s.disk} threshold={thresholds.disk} /></td>
      <td>
        <div className="cell-trend">
          {s.offline ? (
            <span style={{ color: "var(--text-4)" }}>—</span>
          ) : (
            <Sparkline
              data={s.historyCpu}
              color={
                s.status === "crit"
                  ? "var(--crit)"
                  : s.status === "warn"
                    ? "var(--warn)"
                    : "var(--accent)"
              }
            />
          )}
        </div>
      </td>
      <td>
        <div className="flex items-center gap-2">
          <StatusBadge status={s.status} />
          {s.status === "crit" && (
            <span title="Threshold tercapai" style={{ color: "var(--crit)", display: "inline-flex" }}>
              <I.alert />
            </span>
          )}
        </div>
      </td>
      <td onClick={(e) => e.stopPropagation()}>
        <div className="row-actions">
          <button className="btn-icon bare" title="Edit" onClick={onEdit}><I.edit /></button>
          <button className="btn-icon bare danger" title="Hapus" onClick={onDelete}>
            <I.trash />
          </button>
        </div>
      </td>
    </tr>
  );
}

function ServerCard({ server: s, thresholds, pulse, onOpen, onDelete, onEdit }) {
  const cpuStatus = s.offline ? "ok" : metricStatus(s.cpu, thresholds.cpu);
  const ramStatus = s.offline ? "ok" : metricStatus(s.ram, thresholds.ram);
  const diskStatus = s.offline ? "ok" : metricStatus(s.disk, thresholds.disk);
  return (
    <div className="card" data-pulse={pulse || undefined} onClick={onOpen}>
      <div className="card-head">
        <div className="cell-server">
          <span className="server-status-pip" data-status={s.status} />
          <div>
            <div>
              <span className="server-name">{s.name}</span>
              <span className="server-tag" data-role={s.role}>{s.tag}</span>
            </div>
            <div className="server-addr">{s.ip} · {s.region}</div>
          </div>
        </div>
        <StatusBadge status={s.status} />
      </div>
      <div className="card-metrics">
        <CardMetricRow label="CPU" value={s.cpu} offline={s.offline} status={cpuStatus} />
        <CardMetricRow label="RAM" value={s.ram} offline={s.offline} status={ramStatus} />
        <CardMetricRow label="Disk" value={s.disk} offline={s.offline} status={diskStatus} />
      </div>
      <div className="flex items-center justify-between" style={{ marginTop: 4 }}>
        <div className="text-xs text-muted mono">
          {s.lastSeen ? relativeShort(s.lastSeen) : "—"}
        </div>
        <div onClick={(e) => e.stopPropagation()} className="flex gap-2">
          <button className="btn-icon bare" title="Edit" onClick={onEdit}><I.edit /></button>
          <button className="btn-icon bare danger" title="Hapus" onClick={onDelete}>
            <I.trash />
          </button>
        </div>
      </div>
    </div>
  );
}

function CardMetricRow({ label, value, offline, status }) {
  return (
    <div className="card-metric-row">
      <span style={{ color: "var(--text-2)" }}>{label}</span>
      <div className="metric-bar">
        <div className="metric-bar-fill" data-status={status} style={{ width: `${value ?? 0}%` }} />
      </div>
      <span
        className="metric-val"
        style={{ textAlign: "right" }}
        data-status={offline ? "offline" : status}
      >
        {offline ? "—" : Math.round(value) + "%"}
      </span>
    </div>
  );
}

function relativeShort(ts) {
  const diff = Math.floor((Date.now() - ts) / 1000);
  if (diff < 5) return "just now";
  if (diff < 60) return diff + "s ago";
  if (diff < 3600) return Math.floor(diff / 60) + "m ago";
  if (diff < 86400) return Math.floor(diff / 3600) + "h ago";
  return Math.floor(diff / 86400) + "d ago";
}
